const LANGUAGE_STORAGE_KEY = "trainingPreferredLocale";
const LOCALE_LABELS = {
    "en-us": "English",
    "es-us": "Espanol",
    "fil-ph": "Filipino",
    "pt-br": "Português (Brasil)",
};
const LOCALE_FIELD_LABELS = {
    "en-us": "Language",
    "es-us": "Idioma",
    "fil-ph": "Wika",
    "pt-br": "Idioma",
};

function normalizeLocale(raw) {
    if (!raw) return "";
    return String(raw).toLowerCase();
}

function getSupportedLocales(copy) {
    return Object.keys(copy?.locales || {});
}

function getOrderedLocales(copy) {
    const locales = getSupportedLocales(copy);
    const defaultLocale = normalizeLocale(copy?.defaultLocale);
    const ordered = [];
    if (defaultLocale && locales.includes(defaultLocale)) {
        ordered.push(defaultLocale);
    }
    locales.forEach((locale) => {
        if (!ordered.includes(locale)) {
            ordered.push(locale);
        }
    });
    return ordered;
}

function getLocaleLabel(locale) {
    return LOCALE_LABELS[locale] || locale;
}

function getLanguageLabel(locale) {
    return LOCALE_FIELD_LABELS[locale] || "Language";
}

function getStoredLocale(copy) {
    try {
        const stored = normalizeLocale(localStorage.getItem(LANGUAGE_STORAGE_KEY));
        if (stored && copy?.locales?.[stored]) return stored;
    } catch (e) {
        // no-op
    }
    return null;
}

function savePreferredLocale(locale) {
    try {
        if (locale) {
            localStorage.setItem(LANGUAGE_STORAGE_KEY, locale);
        } else {
            localStorage.removeItem(LANGUAGE_STORAGE_KEY);
        }
    } catch (e) {
        // no-op
    }
}

function mapBaseLocale(base, locales) {
    if (!base) return null;
    const normalized = base.toLowerCase();
    if (locales[normalized]) return normalized;
    if (normalized === "en" && locales["en-us"]) return "en-us";
    if (normalized === "es" && locales["es-us"]) return "es-us";
    if (normalized === "pt" && locales["pt-br"]) return "pt-br";
    if (normalized === "tl" && locales["fil-ph"]) return "fil-ph";
    const baseKey = Object.keys(locales).find((k) => k.toLowerCase().startsWith(`${normalized}-`));
    if (baseKey) return baseKey;
    return null;
}

function resolveLocale(copy) {
    const locales = copy?.locales || {};
    const localeKeys = Object.keys(locales);
    if (localeKeys.length === 0) return "en-us";

    const stored = getStoredLocale(copy);
    if (stored) return stored;

    const preferred = (navigator.languages && navigator.languages.length > 0)
        ? navigator.languages[0]
        : (navigator.language || "en-us");
    const normalized = normalizeLocale(preferred);
    if (locales[normalized]) return normalized;
    if (normalized.indexOf("-") > -1) {
        const base = normalized.split("-")[0];
        const mapped = mapBaseLocale(base, locales);
        if (mapped) return mapped;
    }
    const mapped = mapBaseLocale(normalized, locales);
    if (mapped) return mapped;
    return copy?.defaultLocale || localeKeys[0];
}

function getSteps(copy, locale, pageKey) {
    return copy?.locales?.[locale]?.pages?.[pageKey]?.steps || [];
}

function buildLanguagePicker(copy, currentLocale, onChange) {
    const container = document.createElement("div");
    container.id = "training-language-picker";
    container.className = "training-language-picker";

    const label = document.createElement("label");
    label.className = "training-language-label";
    label.setAttribute("for", "training-language-select");
    label.textContent = getLanguageLabel(currentLocale);

    const select = document.createElement("select");
    select.id = "training-language-select";
    select.className = "form-select form-select-sm training-language-select";

    const locales = getOrderedLocales(copy);
    locales.forEach((locale) => {
        const option = document.createElement("option");
        option.value = locale;
        option.textContent = getLocaleLabel(locale);
        select.appendChild(option);
    });
    select.value = currentLocale;
    select.addEventListener("change", () => {
        const nextLocale = normalizeLocale(select.value);
        onChange(nextLocale, { label, select });
    });

    container.appendChild(label);
    container.appendChild(select);
    document.body.appendChild(container);

    return { container, label, select };
}

export async function loadTrainingCopy(url) {
    const response = await fetch(url, { cache: "no-store" });
    if (!response.ok) {
        throw new Error(`Failed to load training copy (${response.status})`);
    }
    return response.json();
}

function buildLibbyPanel() {
    const panel = document.createElement("div");
    panel.id = "training-libby";
    panel.className = "training-libby training-panel-right";

    const header = document.createElement("div");
    header.className = "training-libby-header";

    const avatar = document.createElement("img");
    avatar.src = "/libby.png";
    avatar.alt = "Libby";
    avatar.className = "training-libby-avatar";
    header.appendChild(avatar);

    const name = document.createElement("div");
    name.className = "training-libby-name";
    name.textContent = "Libby";
    header.appendChild(name);

    const title = document.createElement("div");
    title.className = "training-libby-title";

    const body = document.createElement("div");
    body.className = "training-libby-body";

    const footer = document.createElement("div");
    footer.className = "training-libby-footer";

    const backBtn = document.createElement("button");
    backBtn.type = "button";
    backBtn.className = "btn btn-outline-secondary btn-sm";
    backBtn.id = "trainingPrev";
    backBtn.textContent = "Back";

    const nextBtn = document.createElement("button");
    nextBtn.type = "button";
    nextBtn.className = "btn btn-primary btn-sm";
    nextBtn.id = "trainingNext";
    nextBtn.textContent = "Next";

    const stepCounter = document.createElement("span");
    stepCounter.className = "training-libby-steps";
    stepCounter.id = "trainingStepCounter";

    footer.appendChild(backBtn);
    footer.appendChild(stepCounter);
    footer.appendChild(nextBtn);

    panel.appendChild(header);
    panel.appendChild(title);
    panel.appendChild(body);
    panel.appendChild(footer);

    return panel;
}

export function initTrainingOverlay({ copy, pageKey, dashboard, tagToTabIndex = {} }) {
    if (!copy || !pageKey) return;
    let locale = resolveLocale(copy);
    let steps = getSteps(copy, locale, pageKey);
    if (steps.length === 0) return;

    document.body.classList.add("training-active");

    const panel = buildLibbyPanel();
    document.body.appendChild(panel);

    const languagePicker = buildLanguagePicker(copy, locale, (nextLocale, elements) => {
        if (!nextLocale || nextLocale === locale) return;
        const nextSteps = getSteps(copy, nextLocale, pageKey);
        if (nextSteps.length === 0) {
            elements.select.value = locale;
            return;
        }
        locale = nextLocale;
        steps = nextSteps;
        elements.label.textContent = getLanguageLabel(locale);
        savePreferredLocale(locale);
        rebuildTagToStepIndex();
        if (currentIndex >= steps.length) {
            currentIndex = Math.max(steps.length - 1, 0);
        }
        renderStep(currentIndex);
    });

    const titleEl = panel.querySelector(".training-libby-title");
    const bodyEl = panel.querySelector(".training-libby-body");
    const backBtn = panel.querySelector("#trainingPrev");
    const nextBtn = panel.querySelector("#trainingNext");
    const stepCounter = panel.querySelector("#trainingStepCounter");

    let currentIndex = 0;
    let currentTarget = null;
    let isAnimating = false;
    let tagToStepIndex = {};
    function rebuildTagToStepIndex() {
        tagToStepIndex = {};
        steps.forEach((step, idx) => {
            const tag = step?.target_tag;
            if (tag && tagToStepIndex[tag] === undefined) {
                tagToStepIndex[tag] = idx;
            }
        });
    }
    rebuildTagToStepIndex();

    function clearHighlight() {
        if (currentTarget) {
            currentTarget.classList.remove("training-highlight");
            currentTarget = null;
        }
    }

    function positionPanel(target) {
        panel.classList.remove("training-panel-left", "training-panel-right", "training-panel-bottom");
        panel.classList.add("training-panel-right");
        if (!target) {
            panel.style.left = "";
            panel.style.top = "";
            panel.style.right = "24px";
            panel.style.bottom = "24px";
            return;
        }

        const rect = target.getBoundingClientRect();
        panel.style.right = "";
        panel.style.bottom = "";
        panel.style.left = "0px";
        panel.style.top = "0px";
        panel.style.visibility = "hidden";
        panel.style.maxWidth = "360px";

        const panelRect = panel.getBoundingClientRect();
        const spacing = 16;
        let left = rect.right + spacing;
        let top = rect.top;
        let orientation = "right";

        if (left + panelRect.width > window.innerWidth) {
            left = rect.left - spacing - panelRect.width;
            orientation = "left";
        }

        if (left < spacing) {
            left = Math.max(spacing, rect.left);
            top = rect.bottom + spacing;
            orientation = "bottom";
        }

        if (top + panelRect.height > window.innerHeight) {
            top = Math.max(spacing, window.innerHeight - panelRect.height - spacing);
        }

        panel.style.left = `${Math.max(spacing, left)}px`;
        panel.style.top = `${Math.max(spacing, top)}px`;
        panel.style.visibility = "visible";
        panel.classList.add(`training-panel-${orientation}`);
    }

    function highlightTarget(tag) {
        clearHighlight();
        if (!tag) {
            positionPanel(null);
            return;
        }
        const target = document.querySelector(`[data-dashlet-tag=\"${tag}\"]`);
        if (!target) {
            positionPanel(null);
            return;
        }
        target.classList.add("training-highlight");
        currentTarget = target;
        target.scrollIntoView({ behavior: "smooth", block: "center" });
        positionPanel(target);
    }

    function renderStep(index) {
        const step = steps[index];
        if (!step) return;
        currentIndex = index;
        titleEl.textContent = step.title || "Section";
        bodyEl.textContent = step.body || "";
        stepCounter.textContent = `${index + 1} of ${steps.length}`;
        backBtn.disabled = index === 0;
        nextBtn.textContent = index === steps.length - 1 ? "Done" : "Next";

        const targetTag = step.target_tag;
        const tabIndex = tagToTabIndex[targetTag];
        if (typeof tabIndex === "number" && dashboard && dashboard.switchTab) {
            dashboard.switchTab(tabIndex);
            requestAnimationFrame(() => highlightTarget(targetTag));
        } else {
            highlightTarget(targetTag);
        }
    }

    function showStep(index) {
        if (isAnimating) return;
        isAnimating = true;
        panel.classList.add("training-fade");
        setTimeout(() => {
            renderStep(index);
            requestAnimationFrame(() => {
                panel.classList.remove("training-fade");
                setTimeout(() => {
                    isAnimating = false;
                }, 320);
            });
        }, 320);
    }

    backBtn.addEventListener("click", () => {
        if (currentIndex > 0) {
            showStep(currentIndex - 1);
        }
    });

    nextBtn.addEventListener("click", () => {
        if (currentIndex < steps.length - 1) {
            showStep(currentIndex + 1);
        } else {
            document.body.classList.remove("training-active");
            clearHighlight();
            document.removeEventListener("click", handleTrainingClick, true);
            if (tabNav) {
                tabNav.removeEventListener("click", handleTabClick, true);
            }
            panel.remove();
            if (languagePicker?.container) {
                languagePicker.container.remove();
            }
        }
    });

    window.addEventListener("resize", () => {
        positionPanel(currentTarget);
    });

    function handleTrainingClick(event) {
        const libby = document.getElementById("training-libby");
        if (libby && libby.contains(event.target)) return;
        const target = event.target.closest("[data-dashlet-tag]");
        if (!target) return;
        const tag = target.getAttribute("data-dashlet-tag");
        const stepIndex = tagToStepIndex[tag];
        if (stepIndex === undefined || stepIndex === currentIndex) return;
        showStep(stepIndex);
    }

    document.addEventListener("click", handleTrainingClick, true);

    const tabNav = dashboard ? document.getElementById(`${dashboard.divName}_tabs`) : null;
    function handleTabClick(event) {
        if (!tabNav) return;
        const link = event.target.closest(".nav-link");
        if (!link || !tabNav.contains(link)) return;
        clearHighlight();
        positionPanel(null);
    }
    if (tabNav) {
        tabNav.addEventListener("click", handleTabClick, true);
    }

    renderStep(0);
}
